/**
 * MLS-DUAL-001 Phase 2 — MLS Search entry form (mirrors the approved manual
 * Lien Search Report template). Auto-populated read-only header; editable
 * Parcel Number + Date Prepared; repeatable research rows for Code
 * Violations / Open Permits / Utility Account Balances (Trash sub-block
 * inside Utility, per template); section-level "None Found" (keeps
 * office/contact + Notes available, collapses only finding fields).
 * Debounced autosave via PUT /mls-search (merge-on-save backend).
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, History, CheckCircle2, Lock, Download, Landmark, FileCheck2, Droplets, KeyRound } from 'lucide-react';
import { Button } from '../ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import MlsSupportingDocs from './MlsSupportingDocs';
import { useMlsJurisdiction } from './useMlsJurisdiction';
import { MlsServiceNav } from './MlsServiceNav';
import { MlsServiceWorkspace } from './MlsServiceWorkspace';
import { buildSimpleModel, buildUtilityModel } from './mlsServiceModel';
import { inputCls, labelCls } from './MlsFields';
import { withAutoEntities } from './mlsServiceTypes';
import { PERMIT_STATUS_VALUES, UTILITY_PAYMENT_STATUS, UTILITY_ACCOUNT_STATUS } from './mlsAutoFindings';

const API = process.env.REACT_APP_BACKEND_URL;

const UTILITY_FINDINGS = [
  { k: 'account_number', label: 'Acc #' },
  { k: 'balance', label: 'Balance' },
  { k: 'good_thru', label: 'Good Thru', type: 'date' },
  /* MLS-SEARCH-FINDINGS-ENHANCEMENT-001: Payment Status (was free text; legacy text stays readable) + independent Account Status */
  { k: 'status', label: 'Payment Status', type: 'select', options: UTILITY_PAYMENT_STATUS },
  { k: 'account_status', label: 'Account Status', type: 'select', options: UTILITY_ACCOUNT_STATUS },
  { k: 'lienable', label: 'Lien-able', type: 'select', options: ['Yes', 'No', 'N/A'] },
];

const CODE_DEF = {
  key: 'code_violations',
  title: 'Code Violations',
  icon: Landmark,
  tabLabel: 'Code',
  entityField: 'office_holding_violations',
  entityLabel: 'Office Holding Violations',
  addLabel: 'Add Violation Office',
  entryLabel: 'Violation Office',
  findingFields: [
    /* MLS-CV-OPENVIOL-001: Yes / No / N/A saved verbatim; legacy "None" kept readable on old entries only */
    { k: 'open_violations', label: 'Open Violations', type: 'select', options: ['Yes', 'No', 'N/A'], legacyOptions: { None: 'None (legacy)' } },
    { k: 'case_number', label: 'Case #' },
    { k: 'type', label: 'Type' },
    { k: 'amount_owed', label: 'Amount Owed' },
    { k: 'good_thru', label: 'Good Thru', type: 'date' },
  ],
  /* MLS-COND-REQ-001 (mirror of MLS_CONDITIONAL_REQUIRED in backend routes/mls_workflow.py) */
  findingRule: { flag: 'open_violations', positive: ['yes'], required: ['case_number', 'type'], message: 'Case # and Type are required when Open Violations is Yes.' },
};
const PERMIT_DEF = {
  key: 'open_permits',
  title: 'Building Permits',
  subtitle: 'Open / expired permits',
  icon: FileCheck2,
  tabLabel: 'Permit',
  entityField: 'office_holding_permits',
  entityLabel: 'Office Holding Permits',
  addLabel: 'Add Permit Office',
  entryLabel: 'Permit Office',
  findingFields: [
    /* MLS-SEARCH-FINDINGS-ENHANCEMENT-001: "None" retired (legacy value kept readable); N/A ≠ None Found */
    { k: 'open_expired_permit', label: 'Permit Status', type: 'select', options: PERMIT_STATUS_VALUES, legacyOptions: { None: 'None (legacy)' } },
    { k: 'permit_number', label: 'Permit #' },
    { k: 'type', label: 'Type' },
    { k: 'balance_owed', label: 'Balance Owed' },
    { k: 'good_thru', label: 'Good Thru', type: 'date' },
  ],
  findingRule: { flag: 'open_expired_permit', positive: ['open', 'expired'], required: ['permit_number', 'type'], message: 'Permit # and Type are required when an Open or Expired permit is reported.' },
};
/* MLS-UTILITY-001: ONE Utility nav section over the two storage sections (data shape unchanged) */
const UTILITY_DEF = {
  key: 'utility',
  title: 'Utility',
  subtitle: 'Water, sewer & trash accounts',
  icon: Droplets,
  tabLabel: 'Utility',
  addLabel: 'Add utility entry',
  entryLabel: 'Utility entry',
  findingFields: UTILITY_FINDINGS,
};
const UTILITY_ENTRY_DEFS = {
  utility_accounts: { key: 'utility', title: 'Utility', entityField: 'water_sewer_collection_entity', entityLabel: 'Collection Entity', findingFields: UTILITY_FINDINGS },
  trash: { key: 'utility', title: 'Utility', entityField: 'trash_collection_entity', entityLabel: 'Trash Collection Entity', findingFields: UTILITY_FINDINGS },
};
/* storage-level defs (autosave / auto-entity fill / carry-forward keys) */
const STORAGE_DEFS = [CODE_DEF, PERMIT_DEF,
  { key: 'utility_accounts', entityField: 'water_sewer_collection_entity' },
  { key: 'trash', entityField: 'trash_collection_entity' }];
const NAV_DEFS = [CODE_DEF, PERMIT_DEF, UTILITY_DEF];
/* MLS-PARCEL-GATE-002: property-specific finding fields per storage section (+ notes). MUST match
   MLS_FINDING_FIELDS in backend/routes/mls_workflow.py — entity / directory snapshot fields never count as work. */
const FINDING_KEYS = {
  code_violations: [...CODE_DEF.findingFields.map((f) => f.k), 'notes'],
  open_permits: [...PERMIT_DEF.findingFields.map((f) => f.k), 'notes'],
  utility_accounts: [...UTILITY_FINDINGS.map((f) => f.k), 'notes', 'utility_source'], // MLS-UTILITY-WELL-SEPTIC-001
  trash: [...UTILITY_FINDINGS.map((f) => f.k), 'notes'],
};
export const hasPropertyWork = (form) => Object.entries(FINDING_KEYS).some(([k, keys]) => {
  const sec = form?.[k] || {};
  return !!sec.none_found || (sec.entries || []).some((e) => keys.some((f) => String(e?.[f] ?? '').trim()));
});

const emptySection = () => ({ none_found: false, entries: [] });

const normalizeForm = (ms) => ({
  parcel_number: ms?.parcel_number || '',
  date_prepared: ms?.date_prepared || '',
  general_notes: ms?.general_notes || '',
  code_violations: ms?.code_violations || emptySection(),
  open_permits: ms?.open_permits || emptySection(),
  utility_accounts: ms?.utility_accounts || emptySection(),
  trash: ms?.trash || emptySection(),
});

export default function MlsSearchPanel({ orderId, header, mlsSearch, mlsStatus, clientProduct, readOnly = false, onChanged, onSaved, parcelIndex = 1, parcelSelector = null }) {
  const [form, setForm] = useState(() => normalizeForm(mlsSearch));
  const [saveState, setSaveState] = useState('idle'); // idle|saving|saved|error
  const [prevInfo, setPrevInfo] = useState(null);
  const [carryBusy, setCarryBusy] = useState(false);
  const [activeService, setActiveService] = useState('code_violations');
  const [entryIdx, setEntryIdx] = useState({});
  const dirtyRef = useRef(false);
  const timerRef = useRef(null);
  const formRef = useRef(form);
  formRef.current = form;

  const locked = readOnly || mlsStatus === 'complete';
  const isUpdate = ['MLS Update', 'Update MLS'].includes(clientProduct);
  // MLS-PARCEL-GATE-001: the processing area unlocks only once a Parcel # is PERSISTED (saved/autosaved)
  const [persistedParcel, setPersistedParcel] = useState(() => (mlsSearch?.parcel_number || '').trim());
  const [parcelConfirm, setParcelConfirm] = useState(null); // { from, to } while awaiting confirmation
  const persistedRef = useRef(persistedParcel); persistedRef.current = persistedParcel;
  const confirmingRef = useRef(false);
  useEffect(() => { setPersistedParcel((mlsSearch?.parcel_number || '').trim()); }, [mlsSearch?.parcel_number]);
  const gated = !locked && !persistedParcel;
  const [mlsDocCount, setMlsDocCount] = useState(0);
  // work = findings / notes / None Found on any section, or MLS supporting documents attached (same rule as the API)
  const hasServiceData = (f) => hasPropertyWork(f) || mlsDocCount > 0;
  const jur = useMlsJurisdiction(orderId);
  const jurCtx = { orderId, resolution: jur.resolution, reload: jur.reload, setJurisdiction: jur.setJurisdiction, parcelIndex };

  // Skip the reset while local edits are unsaved (in-flight autosave / typing).
  useEffect(() => { if (dirtyRef.current) return; setForm(normalizeForm(mlsSearch)); }, [mlsSearch]);

  // MLS-SVC-DIR-001: routed (mode=auto) entities fill EMPTY entries only. If the
  // component is already in progress the fill is autosaved; before the first
  // real edit it stays local so auto-start is never triggered by the directory.
  useEffect(() => {
    if (locked || !jur.resolution) return;
    setForm((f) => {
      const next = withAutoEntities(f, jur.resolution, STORAGE_DEFS);
      if (next === f) return f;
      if (mlsStatus === 'in_progress' && (f.parcel_number || '').trim()) { // gate: no autosave of service data without a Parcel #
        dirtyRef.current = true;
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(save, 1200);
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jur.resolution, mlsSearch, locked]);

  useEffect(() => {
    if (!isUpdate) return;
    axios.get(`${API}/api/orders/${orderId}/mls-previous`)
      .then((res) => setPrevInfo(res.data))
      .catch(() => setPrevInfo(null));
  }, [orderId, isUpdate]);

  const save = useCallback(async (opts = {}) => {
    setSaveState('saving');
    try {
      const f = formRef.current;
      // MLS-PARCELS-001: every autosave is parcel-addressed (1 = legacy mls_search)
      const res = await axios.put(`${API}/api/orders/${orderId}/mls-search`, {
        parcel_number: f.parcel_number,
        date_prepared: f.date_prepared,
        general_notes: f.general_notes,
        code_violations: f.code_violations,
        open_permits: f.open_permits,
        utility_accounts: f.utility_accounts,
        trash: f.trash,
      }, { params: { parcel: parcelIndex, ...(opts.confirmParcelChange ? { confirm_parcel_change: true } : {}) } });
      // edits made while this PUT was in flight keep the form dirty (prevents the parent reset from reverting them)
      if (formRef.current === f) dirtyRef.current = false;
      setPersistedParcel((f.parcel_number || '').trim());
      onSaved?.(parcelIndex, { ...f, updated_at: new Date().toISOString() }, {
        mls_parcel_present: res.data?.mls_parcel_present,
        mls_completion_blockers: res.data?.mls_completion_blockers,
        mls_mailaway_sections_ready: res.data?.mls_mailaway_sections_ready,
      });
      setSaveState('saved');
      setTimeout(() => setSaveState((s) => (s === 'saved' ? 'idle' : s)), 2500);
    } catch (e) {
      setSaveState('error');
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'parcel_change_confirm') { setSaveState('idle'); setParcelConfirm({ from: detail.from, to: detail.to }); return; }
      if (detail && typeof detail === 'object' && detail.code === 'parcel_clear_blocked') {
        setForm((cur) => ({ ...cur, parcel_number: persistedRef.current })); dirtyRef.current = false;
        toast.warning(detail.message, { duration: 6000 }); return;
      }
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Failed to save MLS Search data'));
    }
  }, [orderId, parcelIndex, onSaved]);

  // Parcel # edits: free while no service data exists; once findings exist, blank is refused and a change needs confirmation.
  const onParcelChange = (value) => {
    if (locked) return;
    const protectedData = !!persistedParcel && hasServiceData(formRef.current);
    if (!protectedData) { update({ parcel_number: value }); return; }
    setForm((f) => ({ ...f, parcel_number: value }));
    if (timerRef.current) clearTimeout(timerRef.current);
  };
  const onParcelBlur = () => {
    if (locked) return;
    const v = (formRef.current.parcel_number || '').trim();
    if (!persistedParcel || !hasServiceData(formRef.current) || v === persistedParcel) return;
    if (!v) {
      setForm((f) => ({ ...f, parcel_number: persistedParcel }));
      toast.warning(`Parcel # ${persistedParcel} cannot be cleared while ${hasPropertyWork(formRef.current) ? 'MLS findings exist' : `${mlsDocCount} MLS supporting document(s) are attached`}. Change it to the correct Parcel # instead, or reset the sections / remove the documents first.`, { duration: 6000 });
      return;
    }
    setParcelConfirm({ from: persistedParcel, to: v });
  };
  const confirmParcelChange = async () => {
    confirmingRef.current = true; // the dialog's onOpenChange(false) must not revert the value we are about to save
    setParcelConfirm(null);
    if (timerRef.current) clearTimeout(timerRef.current); // a pending unconfirmed autosave would 409 again
    dirtyRef.current = true;
    await save({ confirmParcelChange: true });
  };
  const cancelParcelChange = () => { setParcelConfirm(null); setForm((f) => ({ ...f, parcel_number: persistedParcel })); };

  const update = (patch) => {
    if (locked) return;
    setForm((f) => ({ ...f, ...patch }));
    dirtyRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(save, 1200);
  };

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (dirtyRef.current) save();
  }, [save]);

  const carryForward = async () => {
    setCarryBusy(true);
    try {
      const res = await axios.post(`${API}/api/orders/${orderId}/mls-search/carry-forward`);
      toast.success(`Previous MLS data loaded from ${res.data.previous_order_number || 'prior order'} — review every section before completing`);
      onChanged?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Carry-forward failed');
    } finally { setCarryBusy(false); }
  };

  const models = NAV_DEFS.map((d) => (d.key === 'utility' ? buildUtilityModel(d, form, update, UTILITY_ENTRY_DEFS, { enforceSource: !locked }) : buildSimpleModel(d, form, update)));
  const activeModel = models.find((m) => m.key === activeService) || models[0];

  return (
    <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-3.5 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_6px_18px_rgba(15,23,42,0.05)]" data-testid="mls-search-panel">
      {/* MLS-LAYOUT-CLEANUP: Parcel Number + Date Prepared stay (completion validator + PDF header) but live compactly in the title row */}
      <div className="flex items-center justify-between flex-wrap gap-x-4 gap-y-2 mb-2.5">
        <h3 className="text-[15px] font-semibold text-slate-900 flex items-center gap-2">
          <span className="mls-tile bg-gradient-to-br from-blue-600 to-violet-600 text-white shadow-sm"><Landmark className="w-4 h-4" /></span>
          MLS Search Report
          {mlsStatus && !locked && (
            <span className={`inline-flex items-center text-[10px] font-semibold rounded-full px-2 py-0.5 border ${mlsStatus === 'in_progress' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}
              data-testid="mls-search-status-badge">{mlsStatus === 'in_progress' ? 'In Progress' : mlsStatus.replace(/_/g, ' ')}</span>
          )}
          {parcelSelector}
          {locked && (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5"
              data-testid="mls-search-locked-badge">
              <Lock className="w-3 h-3" /> {mlsStatus === 'complete' ? 'MLS Search Complete — read-only' : 'Read-only'}
            </span>
          )}
        </h3>
        <div className="flex items-center gap-3 flex-wrap ml-auto">
          <label className="flex items-center gap-1.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
            Parcel # <span className="text-amber-500">*</span>
            <input type="text" value={form.parcel_number} disabled={locked} placeholder="Parcel number"
              onChange={(e) => onParcelChange(e.target.value)} onBlur={onParcelBlur}
              className={`${inputCls} w-[170px] normal-case tracking-normal font-normal ${gated ? 'ring-2 ring-amber-300 border-amber-400' : ''}`}
              data-testid="mls-parcel-number-input" />
          </label>
          <label className="flex items-center gap-1.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
            Date Prepared <span className="text-amber-500">*</span>
            <input type="date" value={form.date_prepared} disabled={locked}
              onChange={(e) => update({ date_prepared: e.target.value })}
              className={`${inputCls} w-[150px] normal-case tracking-normal font-normal`}
              data-testid="mls-date-prepared-input" />
          </label>
          <span className="text-[11px] text-gray-400 flex items-center gap-1 min-w-[120px] justify-end" data-testid="mls-autosave-indicator">
            {saveState === 'saving' && <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</>}
            {saveState === 'saved' && <><CheckCircle2 className="w-3 h-3 text-green-500" /> Saved</>}
            {saveState === 'error' && <span className="text-red-500">Save failed — changes retried on next edit</span>}
            {saveState === 'idle' && !locked && 'Autosaves as you type'}
          </span>
        </div>
      </div>

      {/* MLS Update — explicit carry-forward (never silent) */}
      {isUpdate && prevInfo && (
        <div className={`mb-3 p-3 rounded-lg border ${prevInfo.linked && prevInfo.previous_order?.has_mls_data
          ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}
          data-testid="mls-update-banner">
          {prevInfo.linked ? (
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-xs text-blue-900 flex items-center gap-1.5">
                <History className="w-3.5 h-3.5" />
                Linked to previous MLS order <strong>{prevInfo.previous_order.order_number}</strong>
                {!prevInfo.previous_order.has_mls_data && ' — no previous MLS data available, complete as new fulfillment'}
              </p>
              {prevInfo.previous_order.has_mls_data && !locked && (
                <Button type="button" size="sm" className="h-7 px-2.5 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={carryBusy} data-testid="mls-carry-forward-btn" onClick={carryForward}>
                  {carryBusy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Download className="w-3 h-3 mr-1" />}
                  Load Previous MLS Data
                </Button>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-600" data-testid="mls-update-no-previous">
              No previous MLS order found for this property — complete as new fulfillment.
            </p>
          )}
        </div>
      )}
      {form.carried_forward_from || mlsSearch?.carried_forward_from ? (
        <div className="mb-3 px-3 py-1.5 rounded-md bg-amber-50 border border-amber-200 text-[11px] text-amber-800"
          data-testid="mls-carried-forward-notice">
          This data was carried forward from a previous MLS order — review and update every section before completing.
        </div>
      ) : null}

      {/* MLS-PARCEL-GATE-001: everything below is disabled (visible, dimmed, non-interactive) until a Parcel # is saved */}
      {gated && (
        <div className="mb-2 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-[12px] text-amber-800" role="status" data-testid="mls-parcel-gate">
          <KeyRound className="w-3.5 h-3.5 shrink-0" />
          <span><strong>Enter Parcel # to begin MLS processing.</strong> {saveState === 'saving' ? 'Saving Parcel #…' : 'Services, entities, findings, documents and actions unlock automatically once the Parcel # is saved.'}</span>
        </div>
      )}
      {/* MLS-SVC-NAV-001: left = which service · right = ONE dynamic workspace for that service */}
      <div className={`flex items-start gap-3 transition-opacity duration-200 ${gated ? 'opacity-60 pointer-events-none select-none' : ''}`} aria-disabled={gated || undefined} data-testid="mls-workspace" data-gated={gated ? 'true' : 'false'}>
        <MlsServiceNav models={models} active={activeModel.key} onSelect={setActiveService}
          footer={(
            /* MLS-DOCS-COMPACT: documents belong to the whole MLS Search → live in the left column, reachable from every service.
               MLS-JUR-UI-001: the Jurisdiction Directory block moved into ENTITY & CONTACT (Change Jurisdiction).
               MLS-DOCS-LOCK-001: docs follow the report lock (complete ⇒ view-only), not just the caller's readOnly. */
            <MlsSupportingDocs orderId={orderId} readOnly={locked || gated} onCountChange={setMlsDocCount} activeService={activeModel?.key} />
          )} />
        <MlsServiceWorkspace key={activeModel.key} model={activeModel} disabled={locked || gated} saveState={saveState}
          resolution={jur.resolution} jurCtx={jurCtx}
          activeEntry={entryIdx[activeModel.key] || 0} onActiveEntry={(i) => setEntryIdx((m) => ({ ...m, [activeModel.key]: i }))} />
      </div>
      <AlertDialog open={!!parcelConfirm} onOpenChange={(o) => { if (!o && !confirmingRef.current) cancelParcelChange(); if (!o) confirmingRef.current = false; }}>
        <AlertDialogContent data-testid="mls-parcel-change-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Change Parcel # on this MLS record?</AlertDialogTitle>
            <AlertDialogDescription>
              Parcel # will change from <strong>{parcelConfirm?.from}</strong> to <strong>{parcelConfirm?.to}</strong>. All MLS findings already entered stay attached to this parcel record — nothing is cleared or moved. The change is recorded in the order activity.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelParcelChange} data-testid="mls-parcel-change-cancel">Keep {parcelConfirm?.from}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmParcelChange} className="bg-amber-600 hover:bg-amber-700" data-testid="mls-parcel-change-confirm">Change Parcel #</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* MLS-LAYOUT-CLEANUP: General Notes retired from entry (per-entry Notes cover it); legacy text stays visible + printed */}
      {(form.general_notes || '').trim() ? (
        <div className="mt-2.5 rounded-md border border-slate-200 bg-slate-50/70 px-3 py-2" data-testid="mls-general-notes-legacy">
          <label className={labelCls}>General Notes <span className="normal-case tracking-normal font-normal text-slate-400">(legacy — still printed on the report)</span></label>
          <textarea rows={2} value={form.general_notes} disabled={locked}
            onChange={(e) => update({ general_notes: e.target.value })}
            className={`${inputCls} h-auto py-1.5 resize-y`}
            data-testid="mls-general-notes-input" />
        </div>
      ) : null}
      {/* MLS-WF-PREMIUM-002: the inline "Still required…" banner was removed — outstanding requirements
          live only in the Complete MLS Search checklist under Workflow Actions. */}
    </div>
  );
}
